import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trpc } from "../lib/trpc.js";
import { Button } from "../components/ui/Button.js";
import { Input } from "../components/ui/Input.js";
import { NavActions } from "../components/NavActions.js";

function CreateClubForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState<"PRIVATE" | "PUBLIC">("PRIVATE");
  const [description, setDescription] = useState("");
  const utils = trpc.useUtils();
  const { t } = useTranslation();

  const create = trpc.clubs.create.useMutation({
    onMutate: async (vars) => {
      await utils.clubs.list.cancel();
      const prev = utils.clubs.list.getData();
      utils.clubs.list.setData(undefined, (old) => {
        const item = {
          id: `optimistic-${Date.now()}`,
          name: vars.name,
          description: vars.description || null,
          visibility: vars.visibility,
          role: "ADMIN" as const,
          memberCount: 1,
          joinedAt: new Date().toISOString(),
        };
        return [...(old ?? []), item] as typeof old;
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) utils.clubs.list.setData(undefined, ctx.prev); },
    onSuccess: () => {
      utils.clubs.list.invalidate();
      onDone();
    },
  });

  return (
    <form
      className="mt-4 rounded-xl p-5 space-y-4"
      style={{
        background: "var(--color-card)",
        border: "1px solid var(--color-border)",
        boxShadow: "var(--shadow-md)",
        animation: "slide-up-sm 0.25s cubic-bezier(0.16, 1, 0.3, 1) both",
      }}
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate({ name, visibility, description: description || undefined });
      }}
    >
      <h3
        className="font-serif font-semibold text-base"
        style={{ color: "var(--color-foreground)", letterSpacing: "-0.01em" }}
      >
        {t("dashboard.newClubForm.title")}
      </h3>
      <Input
        label={t("dashboard.newClubForm.nameLabel")}
        required
        minLength={1}
        maxLength={100}
        placeholder="e.g. Sunday Pages"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className="flex flex-col gap-1.5">
        <label
          className="text-xs font-medium uppercase tracking-widest"
          style={{ color: "var(--color-muted-foreground)" }}
        >
          {t("dashboard.newClubForm.visibilityLabel")}
        </label>
        <select
          className="flex h-10 w-full rounded-md border px-3 py-2 text-sm transition-all focus-visible:outline-none focus-visible:ring-2"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-card)",
            color: "var(--color-foreground)",
            boxShadow: "var(--shadow-xs)",
          }}
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as "PRIVATE" | "PUBLIC")}
        >
          <option value="PRIVATE">{t("dashboard.newClubForm.visibilityPrivate")}</option>
          <option value="PUBLIC">{t("dashboard.newClubForm.visibilityPublic")}</option>
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <label
          className="text-xs font-medium uppercase tracking-widest"
          style={{ color: "var(--color-muted-foreground)" }}
        >
          {t("club.description.placeholder")}
        </label>
        <textarea
          maxLength={280}
          placeholder={t("club.description.placeholder")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="flex w-full rounded-md border px-3 py-2 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 resize-none"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-card)",
            color: "var(--color-foreground)",
            boxShadow: "var(--shadow-xs)",
            minHeight: "80px",
          }}
        />
        <div
          className="text-xs"
          style={{ color: "var(--color-muted-foreground)" }}
        >
          {description.length}/280
        </div>
      </div>
      {create.error && (
        <p className="text-xs" style={{ color: "var(--color-destructive)" }}>
          {create.error.message}
        </p>
      )}
      <div className="flex gap-2 pt-1">
        <Button type="submit" loading={create.isPending}>{t("dashboard.newClubForm.submit")}</Button>
        <Button type="button" variant="ghost" onClick={onDone}>{t("dashboard.newClubForm.cancel")}</Button>
      </div>
    </form>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const { t } = useTranslation();

  const { data: me } = trpc.auth.me.useQuery();
  const { data: clubs, isLoading } = trpc.clubs.list.useQuery();


  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 md:px-10"
        style={{
          background: "oklch(97% 0.014 82 / 0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <span
          className="font-serif text-lg font-semibold tracking-tight"
          style={{ color: "var(--color-foreground)" }}
        >
          {t("nav.logo")}
        </span>
        <NavActions />
      </header>

      {/* Main */}
      <main className="mx-auto max-w-2xl px-6 py-10 md:px-10">
        {/* Page title row */}
        <div
          className="flex items-end justify-between mb-2"
          style={{ animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both" }}
        >
          <div>
            <p
              className="text-xs font-medium uppercase tracking-widest mb-1"
              style={{ color: "var(--color-accent)" }}
            >
              {t("dashboard.tagline")}
            </p>
            <h1
              className="font-serif font-semibold"
              style={{ fontSize: "1.75rem", letterSpacing: "-0.02em", color: "var(--color-foreground)" }}
            >
              {me ? t("dashboard.greeting", { name: me.displayName.split(" ")[0] }) : "Dashboard"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {!creating && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/search")}
                style={{ color: "var(--color-muted-foreground)" }}
              >
                {t("clubSearch.discoverLink")}
              </Button>
            )}
            {!creating && (
              <Button onClick={() => setCreating(true)} size="sm">
                {t("dashboard.newClub")}
              </Button>
            )}
          </div>
        </div>

        {/* Thin rule */}
        <div
          className="mb-6 mt-4 h-px"
          style={{
            background: "linear-gradient(to right, var(--color-accent), transparent)",
            opacity: 0.4,
            animation: "fade-in 0.5s ease-out 0.1s both",
          }}
        />

        {creating && <CreateClubForm onDone={() => setCreating(false)} />}

        {/* Club list */}
        <div className="mt-6 space-y-3">
          {isLoading && (
            <div className="flex items-center gap-2 py-8 justify-center">
              <span
                className="h-4 w-4 rounded-full border-2 border-current border-t-transparent"
                style={{ color: "var(--color-accent)", animation: "spin 0.7s linear infinite" }}
              />
              <span className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>{t("dashboard.loadingClubs")}</span>
            </div>
          )}

          {!isLoading && clubs?.length === 0 && !creating && (
            <div
              className="text-center py-14 rounded-xl"
              style={{
                background: "var(--color-card)",
                border: "1px dashed var(--color-border-strong)",
              }}
            >
              <p
                className="font-serif italic text-lg mb-2"
                style={{ color: "var(--color-muted-foreground)" }}
              >
                {t("dashboard.createClub")}
              </p>
              <Button onClick={() => setCreating(true)}>{t("dashboard.createClubCta")}</Button>
            </div>
          )}

          {clubs?.map((club, i) => (
            <button
              key={club.id}
              className="book-spine w-full text-left rounded-xl px-5 py-4 transition-all duration-150 hover:shadow-[var(--shadow-md)] group"
              style={{
                background: "var(--color-card)",
                border: "1px solid var(--color-border)",
                boxShadow: "var(--shadow-sm)",
                animation: `slide-up-sm 0.3s cubic-bezier(0.16, 1, 0.3, 1) ${0.05 + i * 0.06}s both`,
                cursor: "pointer",
              }}
              onClick={() => navigate(`/clubs/${club.id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 pl-3">
                  <p
                    className="font-serif font-medium text-base truncate"
                    style={{ color: "var(--color-foreground)", letterSpacing: "-0.01em" }}
                  >
                    {club.name}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                      {t("dashboard.memberCount", { count: club.memberCount })}
                      {" · "}
                      {club.visibility === "PRIVATE" ? t("club.visibility.private") : t("club.visibility.public")}
                      {" · "}
                      <span style={{ color: club.role === "ADMIN" ? "var(--color-accent)" : undefined }}>
                        {club.role === "ADMIN" ? t("dashboard.roleAdmin") : t("dashboard.roleMember")}
                      </span>
                    </p>
                    {club.type && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          background: "var(--color-muted)",
                          color: "var(--color-muted-foreground)",
                        }}
                      >
                        {t(`clubType.${club.type}`)}
                      </span>
                    )}
                    {(club.locationCity || club.locationCountry) && (
                      <span
                        className="text-xs"
                        style={{ color: "var(--color-muted-foreground)" }}
                      >
                        {[club.locationCity, club.locationCountry].filter(Boolean).join(", ")}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className="ml-3 text-xs opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  style={{ color: "var(--color-muted-foreground)" }}
                  aria-hidden
                >
                  {t("dashboard.openClub")}
                </span>
              </div>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
